/**
 * Shop drawings ⇄ Excel: read an import file (.xlsx "Shop Drawings" sheet, .csv, .tsv), write the
 * current view to .xlsx, and build the import template. No dependencies beyond fflate.
 */
import { strToU8, unzipSync, zipSync } from 'fflate';
import { DEFAULT_SHOP_DRAWING_DISCIPLINES } from '@/contexts/AppSettingsContext';
import { formatDate } from '@/lib/format';
import {
  BLOCKERS,
  BLOCKER_LABELS,
  REPLY_LABELS,
  STATUSES,
  STATUS_LABELS,
  blockerMeta,
  buildSubmissionRows,
  isEmptyNote,
  labelize,
  realDrawingId,
  replyLabel,
} from './model';

export const IMPORT_SHEET_NAME = 'Shop Drawings';
const DISCIPLINES = DEFAULT_SHOP_DRAWING_DISCIPLINES;

/** Columns the import reads (also the template's header row). */
export const IMPORT_HEADERS = [
  '#', 'Project', 'Zone', 'Discipline', 'Floor / Area', 'Unit Description', 'Drawing ID', 'Drawing Type',
  'Status', 'Blocker Type', 'Action Required', 'Responsible', 'Drawn by', 'Last Updated', 'Submitted', 'PATH', 'Notes', 'Rev',
];

const EXPORT_HEADERS = [
  'Project No.', 'Project', 'Discipline', 'Block', 'Floor / Area', 'Description', 'Drawing Type', 'Drawing ID',
  'Revision', 'Status', 'Blocker', 'Action Required', 'Responsible', 'Target Date', 'Updated By', 'Last Updated',
  'PDF', 'DWG', 'Notes', 'Submission Count', 'Latest Submission Reply', 'Internal ID',
];

export const SUBMISSION_EXPORT_HEADERS = [
  'Project No.', 'Project', 'Discipline', 'Block', 'Floor / Area', 'Description', 'Drawing ID', 'Internal ID',
  'Date Sent', 'Date as typed', 'Sent To', 'Sent By', 'Revision', 'Consultant Reply', 'Reply Date', 'Consultant Comments',
  'Submission Comments', 'PDF', 'Drawing Status',
];

/* ------------------------------------------------------------------ import */

const normalizeImportHeader = (value) => String(value || '').trim().toLowerCase().replace(/\s*\/\s*/g, ' / ').replace(/\s+/g, ' ');
const importCell = (row, header) => String(row[normalizeImportHeader(header)] || '').trim();
const EMPTY_IMPORT_TOKENS = new Set(['', 'EMPTY']);
const normalizeEnumToken = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const normalizeImportDiscipline = (value) => {
  const token = normalizeEnumToken(value);
  if (EMPTY_IMPORT_TOKENS.has(token)) return 'OTHER';
  return DISCIPLINES.includes(token) ? token : 'OTHER';
};

const normalizeImportStatus = (value) => {
  const token = normalizeEnumToken(value);
  if (EMPTY_IMPORT_TOKENS.has(token)) return 'NOT_STARTED';
  return STATUSES.includes(token) ? token : 'NOT_STARTED';
};

const normalizeImportBlocker = (value) => {
  const token = normalizeEnumToken(value);
  if (EMPTY_IMPORT_TOKENS.has(token)) return 'NONE';
  return BLOCKERS.includes(token) ? token : 'NONE';
};

const buildImportNotes = (pathValue, notesValue) => [pathValue ? `PATH: ${pathValue}` : '', notesValue].filter(Boolean).join('\n');

const todayDayFirst = () => new Date().toLocaleDateString('en-GB');

// The sheet has no consultant reply column. A row with Submitted = YES gets a submission whose reply
// follows the drawing status (an approved drawing was approved; anything else is still waiting) —
// never a blanket "Approved" (the backend applies the same rule).
const IMPORT_REPLY_BY_STATUS = {
  APPROVED: 'APPROVED',
  COMPLETED: 'APPROVED',
  APPROVED_WITH_COMMENTS: 'APPROVED_WITH_COMMENTS',
  COMPLETED_WITH_REMARKS: 'APPROVED_WITH_COMMENTS',
  REJECTED: 'REJECTED',
};
export const importReplyForStatus = (status) => IMPORT_REPLY_BY_STATUS[String(status || '').toUpperCase()] || 'PENDING';

export function mapImportRow(row) {
  const desc = importCell(row, 'Unit Description');
  if (!desc || desc.toUpperCase() === 'EMPTY') return null;
  const disciplineToken = normalizeEnumToken(importCell(row, 'Discipline'));
  const statusToken = normalizeEnumToken(importCell(row, 'Status'));
  if (EMPTY_IMPORT_TOKENS.has(disciplineToken) || EMPTY_IMPORT_TOKENS.has(statusToken)) return null;
  const updated = importCell(row, 'Last Updated');
  const updby = importCell(row, 'Drawn by');
  const rev = importCell(row, 'Rev');
  const imported = {
    project_no: importCell(row, '#'),
    project: importCell(row, 'Project'),
    block: importCell(row, 'Zone'),
    disc: normalizeImportDiscipline(importCell(row, 'Discipline')),
    floor: importCell(row, 'Floor / Area'),
    desc,
    drawingId: importCell(row, 'Drawing ID') || 'NO ID',
    dtype: importCell(row, 'Drawing Type') || 'Floor Plan',
    status: normalizeImportStatus(importCell(row, 'Status')),
    blocker: normalizeImportBlocker(importCell(row, 'Blocker Type')),
    action: importCell(row, 'Action Required'),
    resp: importCell(row, 'Responsible'),
    updby,
    updated,
    pdf: '',
    dwg: '',
    notes: buildImportNotes(importCell(row, 'PATH'), importCell(row, 'Notes')),
    rev,
    change_note: 'Imported from Excel',
  };

  if (importCell(row, 'Submitted').toUpperCase() === 'YES') {
    imported.submissions = [{
      to: 'Consultant',
      date: updated || todayDayFirst(),
      rev,
      pdf: '',
      reply: importReplyForStatus(imported.status),
      comments: 'Imported from Submitted = YES',
      by: updby || 'Import',
    }];
  }

  return imported;
}

const rowsToImportObjects = (rows) => {
  if (rows.length < 2) return [];
  const headers = rows[0].map(normalizeImportHeader);
  return rows.slice(1).map((raw) => {
    const row = Object.fromEntries(headers.map((header, index) => [header, String(raw[index] ?? '').trim()]).filter(([header]) => header));
    return mapImportRow(row);
  }).filter(Boolean);
};

const readFileAsArrayBuffer = (file) => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsText = (file) => {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsText(file);
  });
};

const parseDelimitedLine = (line, separator) => {
  const cells = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === '"' && quoted && next === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === separator && !quoted) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

const parseDelimitedImportFile = async (file) => {
  const text = await readFileAsText(file);
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) return [];
  const separator = lines[0].includes('\t') ? '\t' : ',';
  return rowsToImportObjects(lines.map((line) => parseDelimitedLine(line, separator)));
};

const textFromZipEntry = (entries, path) => {
  const bytes = entries[path];
  if (!bytes) return '';
  return new TextDecoder('utf-8').decode(bytes);
};

const parseXml = (xml) => new DOMParser().parseFromString(xml, 'application/xml');
const attr = (node, name) => node?.getAttribute(name) || '';
const localName = (node) => node?.localName || String(node?.nodeName || '').split(':').pop();
const elementsByName = (root, name) => [...(root?.getElementsByTagName('*') || [])].filter((node) => localName(node) === name);

const columnIndexFromRef = (ref) => {
  const letters = String(ref || '').match(/^[A-Z]+/i)?.[0] || '';
  return letters.toUpperCase().split('').reduce((total, char) => (total * 26) + char.charCodeAt(0) - 64, 0) - 1;
};

const readSharedStrings = (entries) => {
  const xml = textFromZipEntry(entries, 'xl/sharedStrings.xml');
  if (!xml) return [];
  const doc = parseXml(xml);
  return elementsByName(doc, 'si').map((item) => elementsByName(item, 't').map((text) => text.textContent || '').join(''));
};

const findSheetPath = (entries, sheetName) => {
  const workbookXml = textFromZipEntry(entries, 'xl/workbook.xml');
  const relsXml = textFromZipEntry(entries, 'xl/_rels/workbook.xml.rels');
  if (!workbookXml || !relsXml) throw new Error('Invalid Excel workbook');

  const workbook = parseXml(workbookXml);
  const rels = parseXml(relsXml);
  const relTargets = new Map(elementsByName(rels, 'Relationship').map((rel) => [attr(rel, 'Id'), attr(rel, 'Target')]));
  const sheet = elementsByName(workbook, 'sheet').find((entry) => attr(entry, 'name').trim().toLowerCase() === sheetName.toLowerCase());
  if (!sheet) throw new Error(`Sheet "${sheetName}" was not found`);
  const relId = attr(sheet, 'r:id') || attr(sheet, 'id');
  const target = relTargets.get(relId);
  if (!target) throw new Error(`Sheet "${sheetName}" was not found`);
  return target.startsWith('xl/') ? target : `xl/${target.replace(/^\/+/, '')}`;
};

const readCellText = (cell, sharedStrings) => {
  const type = attr(cell, 't');
  if (type === 'inlineStr') {
    return elementsByName(cell, 't').map((node) => node.textContent || '').join('');
  }
  const value = elementsByName(cell, 'v')[0]?.textContent || '';
  if (type === 's') return sharedStrings[Number(value)] || '';
  return value;
};

const parseXlsxImportFile = async (file) => {
  const entries = unzipSync(new Uint8Array(await readFileAsArrayBuffer(file)));
  const sharedStrings = readSharedStrings(entries);
  const sheetPath = findSheetPath(entries, IMPORT_SHEET_NAME);
  const sheetXml = textFromZipEntry(entries, sheetPath);
  if (!sheetXml) throw new Error(`Sheet "${IMPORT_SHEET_NAME}" was not found`);
  const sheet = parseXml(sheetXml);
  const rows = elementsByName(sheet, 'row').map((row) => {
    const values = [];
    elementsByName(row, 'c').forEach((cell, fallbackIndex) => {
      const index = Math.max(columnIndexFromRef(attr(cell, 'r')), fallbackIndex);
      values[index] = readCellText(cell, sharedStrings);
    });
    return values.map((value) => value || '');
  }).filter((row) => row.some((value) => String(value || '').trim()));
  return rowsToImportObjects(rows);
};

/** Parse an import file (.xlsx/.xls → "Shop Drawings" sheet; anything else as CSV/TSV). */
export async function parseImportFile(file) {
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  return ['xlsx', 'xls'].includes(extension) ? parseXlsxImportFile(file) : parseDelimitedImportFile(file);
}

/* ------------------------------------------------------------------ export */

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const columnName = (index) => {
  let name = '';
  let current = index + 1;
  while (current > 0) {
    current -= 1;
    name = String.fromCharCode(65 + (current % 26)) + name;
    current = Math.floor(current / 26);
  }
  return name;
};

export const rowsToXlsx = (rows, sheetName = IMPORT_SHEET_NAME) => {
  const sheetRows = rows.map((row, rowIndex) => (
    `<row r="${rowIndex + 1}">${row.map((value, colIndex) => (
      `<c r="${columnName(colIndex)}${rowIndex + 1}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`
    )).join('')}</row>`
  )).join('');
  return zipSync({
    '[Content_Types].xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
      + '</Types>'),
    '_rels/.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
      + '</Relationships>'),
    'xl/workbook.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
      + `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>`
      + '</workbook>'),
    'xl/_rels/workbook.xml.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
      + '</Relationships>'),
    'xl/worksheets/sheet1.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
      + `<sheetData>${sheetRows}</sheetData>`
      + '</worksheet>'),
  });
};

/**
 * The reply as the Submission Log shows it — 'Awaiting reply', 'Not recorded', 'B · Approved w/
 * comments' — never the stored value alone: the Excel import stored 'Approved' on submissions that
 * nobody replied to. When the reply came from the import, it says so.
 */
export const replyExportText = (row) => {
  const label = replyLabel(row);
  return row.importReply && row.replyState !== 'replied'
    ? `${label} (import said ${labelize(row.importReply, REPLY_LABELS)})`
    : label;
};

const latestReplyText = (drawing) => {
  const rows = buildSubmissionRows([drawing]);
  return rows.length ? replyExportText(rows[rows.length - 1]) : '';
};

/**
 * One register row. A Blocked drawing without a reason says "Reason missing" (as the screen does), never "None".
 * `blocked` (the Blocked tab's export): an Action note that says nothing ('NONE', '-') is left blank, like the "—"
 * on the screen.
 */
const drawingToExportRow = (drawing, { blocked = false } = {}) => {
  const submissions = drawing.submissions || [];
  const reason = blockerMeta(drawing);
  return [
    drawing.project_no || '',
    drawing.project || '',
    drawing.disc || '',
    drawing.block || '',
    drawing.floor || '',
    drawing.desc || '',
    drawing.dtype || '',
    realDrawingId(drawing.drawingId) || 'NO ID',
    drawing.rev || '',
    labelize(drawing.status, STATUS_LABELS),
    reason.missing ? reason.label : labelize(drawing.blocker, BLOCKER_LABELS),
    blocked && isEmptyNote(drawing.action) ? '' : drawing.action || '',
    drawing.resp || '',
    drawing.target || '',
    drawing.updby || '',
    drawing.updated || '',
    drawing.pdf || '',
    drawing.dwg || '',
    drawing.notes || '',
    String(submissions.length),
    latestReplyText(drawing),
    drawing.id || '',
  ];
};

const submissionToExportRow = (row) => [
  row.drawing.project_no || '',
  row.drawing.project || '',
  row.drawing.disc || '',
  row.drawing.block || '',
  row.drawing.floor || '',
  row.drawing.desc || '',
  realDrawingId(row.drawing.drawingId) || 'NO ID',
  row.drawing.id || '',
  row.sent.ok ? formatDate(row.sent.iso) : '',
  row.sent.raw || '',
  row.to,
  row.by,
  row.submission.rev || '',
  replyExportText(row),
  row.replyDate?.label || '',
  row.replyComments || '',
  row.comments,
  row.submission.pdf || '',
  labelize(row.drawing.status, STATUS_LABELS),
];

export const drawingsToXlsx = (drawings, options = {}) => rowsToXlsx([EXPORT_HEADERS, ...drawings.map((drawing) => drawingToExportRow(drawing, options))]);
export const submissionsToXlsx = (rows) => rowsToXlsx([SUBMISSION_EXPORT_HEADERS, ...rows.map(submissionToExportRow)], 'Submission Log');

/** The import template: header row + one example row. */
export const templateToXlsx = () => rowsToXlsx([
  IMPORT_HEADERS,
  ['69', 'LUXOR TOWER', 'Tower', 'BMS', 'GROUND FLOOR', 'GROUND FLOOR - DDC', 'NO ID', 'Wiring Diagram', 'NOT_STARTED', 'NONE', '', 'mohanad', 'Yahya', '16/09/2026', 'NO', '', '', 'R00'],
]);

/** Trigger a browser download of xlsx bytes. */
export function downloadXlsx(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
